import { Link } from "react-router-dom";
import { FileCheck, BookOpen, FileText, ExternalLink, PenLine } from "lucide-react";
import { useGet } from "../lib/hooks";
import { auDate, fmtDate, download } from "../lib/api";
import { PageHeader } from "../components/Common";
import { Button } from "../components/ui/button";

function Section({ title, icon: Icon, children, empty, tid }) {
  return (
    <section className="rounded-md border border-[#C2A462]/20 bg-card" data-testid={tid}>
      <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3"><Icon className="h-4 w-4 text-[#C2A462]" /><span className="overline">{title}</span></div>
      <div className="divide-y divide-white/5">{children}</div>
      {empty && <div className="p-6 text-sm text-muted-foreground">{empty}</div>}
    </section>
  );
}

export default function DocumentEssentials() {
  const [d] = useGet("/essentials");
  if (!d) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  const drafts = new Set(d.drafts.map((x) => x.form_id));
  return (
    <div data-testid="essentials-page">
      <PageHeader overline="Workspace" title="Document Essentials" desc="Approved forms to fill in, and the policies and procedures shared with you." />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Forms to fill in" icon={FileCheck} tid="essentials-forms" empty={!d.forms.length && "No approved forms have been shared with you yet."}>
          {d.forms.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`essential-form-${f.id}`}>
              <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{f.name}</div>
                <div className="text-xs text-muted-foreground">Version {f.version || "1.0"}{f.next_review_date ? ` · Review ${auDate(f.next_review_date)}` : ""}{drafts.has(f.id) ? " · Draft saved" : ""}</div></div>
              <Button size="sm" asChild data-testid={`essential-fill-${f.id}`}><Link to={`/app/forms/fill/${f.id}`}><PenLine className="h-3.5 w-3.5 mr-1" />{drafts.has(f.id) ? "Continue" : "Fill in"}</Link></Button>
            </div>
          ))}
        </Section>
        <Section title="Policies & procedures" icon={BookOpen} tid="essentials-docs" empty={!d.documents.length && "No active policies or procedures yet. Add them in Record Centre › Document Register."}>
          {d.documents.map((x) => (
            <div key={x.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`essential-doc-${x.id}`}>
              <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{x.title}</div>
                <div className="text-xs text-muted-foreground">{x.category}{x.version ? ` · v${x.version}` : ""}{x.review_date ? ` · Review ${auDate(x.review_date)}` : ""}</div></div>
              {x.sharepoint_url && <a href={x.sharepoint_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#C2A462]" data-testid={`essential-doc-open-${x.id}`}>Open <ExternalLink className="h-3 w-3" /></a>}
            </div>
          ))}
        </Section>
        <Section title="My completed forms" icon={FileText} tid="essentials-submissions" empty={!d.submissions.length && "Forms you submit will appear here."}>
          {d.submissions.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-4 py-3" data-testid={`essential-sub-${s.id}`}>
              <div className="min-w-0 flex-1"><div className="truncate text-sm">{s.form_name}</div><div className="text-xs text-muted-foreground">{fmtDate(s.created_at)}</div></div>
              {s.pdf ? <Button size="sm" variant="secondary" onClick={() => download(`/essentials/submissions/${s.id}/pdf`, `${s.form_name}-${s.created_at.slice(0, 10)}.pdf`)} data-testid={`essential-sub-pdf-${s.id}`}>PDF</Button> : <span className="text-[11px] text-muted-foreground">PDF preparing…</span>}
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}
